import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, Search, Edit, Trash2, Eye, Copy, Download, Upload, 
  BarChart3, TrendingUp, TrendingDown, Activity, FileStack
} from "lucide-react";
import { format } from "date-fns";
import { auditLog } from "@/lib/adminAuditLog";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkWorkflowOperations } from "@/components/admin/BulkWorkflowOperations";
import { ImportWorkflowDialog } from "@/components/admin/ImportWorkflowDialog";

interface Workflow {
  id: string;
  workflow_key: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  is_active: boolean;
  created_at: string;
  version: number;
  usage_count: number;
  completion_rate: number;
  abandonment_rate: number;
  phases: any;
}

export default function WorkflowTemplateManager() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState<Workflow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>([]);
  const [showBulkOps, setShowBulkOps] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadWorkflows();
  }, []);

  useEffect(() => {
    filterWorkflows();
  }, [searchQuery, categoryFilter, statusFilter, workflows]);

  async function loadWorkflows() {
    try {
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWorkflows((data || []) as Workflow[]);
    } catch (error: any) {
      console.error('Error loading workflows:', error);
      toast({
        title: "Error",
        description: "Failed to load workflows",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  function filterWorkflows() {
    let filtered = workflows;

    if (searchQuery) {
      filtered = filtered.filter(w =>
        w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.workflow_key.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (categoryFilter !== "all") {
      filtered = filtered.filter(w => w.category === categoryFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(w => 
        statusFilter === "active" ? w.is_active : !w.is_active
      );
    }

    setFilteredWorkflows(filtered);
  }

  async function handleDelete(workflowId: string, title: string) {
    if (!confirm(`Are you sure you want to delete workflow "${title}"? This cannot be undone.`)) return;

    try {
      const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', workflowId);

      if (error) throw error;

      await auditLog.workflowUpdated(workflowId, { action: 'deleted', title });

      toast({
        title: "Workflow Deleted",
        description: "Workflow has been permanently deleted",
      });

      await loadWorkflows();
    } catch (error: any) {
      console.error('Error deleting workflow:', error);
      toast({
        title: "Error",
        description: "Failed to delete workflow",
        variant: "destructive",
      });
    }
  }

  async function handleDuplicate(workflowId: string) {
    try {
      const { data: original, error: fetchError } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', workflowId)
        .single();

      if (fetchError) throw fetchError;

      const duplicate = {
        ...original,
        id: undefined,
        workflow_key: `${original.workflow_key}-copy`,
        title: `${original.title} (Copy)`,
        is_active: false,
        created_at: undefined,
        updated_at: undefined,
        usage_count: 0,
        completion_rate: null,
        abandonment_rate: null,
      };

      const { data, error: insertError } = await supabase
        .from('workflows')
        .insert(duplicate)
        .select()
        .single();

      if (insertError) throw insertError;

      await auditLog.workflowUpdated(data.id, { 
        action: 'duplicated', 
        title: duplicate.title,
        source_id: workflowId 
      });

      toast({
        title: "Workflow Duplicated",
        description: "You can now edit the duplicated workflow",
      });

      navigate(`/admin/templates/workflows/${data.id}/edit`);
    } catch (error: any) {
      console.error('Error duplicating workflow:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate workflow",
        variant: "destructive",
      });
    }
  }

  async function handleToggleActive(workflowId: string, currentStatus: boolean) {
    try {
      const { error } = await supabase
        .from('workflows')
        .update({ is_active: !currentStatus })
        .eq('id', workflowId);

      if (error) throw error;

      await auditLog.workflowUpdated(workflowId, { 
        action: 'status_changed',
        new_status: !currentStatus 
      });

      toast({
        title: "Status Updated",
        description: `Workflow ${!currentStatus ? 'activated' : 'deactivated'}`,
      });

      await loadWorkflows();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update workflow status",
        variant: "destructive",
      });
    }
  }

  function exportWorkflow(workflow: Workflow) {
    const dataStr = JSON.stringify(workflow, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `workflow-${workflow.workflow_key}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  const totalWorkflows = workflows.length;
  const activeWorkflows = workflows.filter(w => w.is_active).length;
  const avgCompletionRate = workflows.length > 0
    ? workflows.reduce((acc, w) => acc + (w.completion_rate || 0), 0) / workflows.length
    : 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workflow Template Manager</h1>
          <p className="text-muted-foreground">Create and manage guided workflow templates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          {selectedWorkflows.length > 0 && (
            <Button variant="outline" onClick={() => setShowBulkOps(true)}>
              <FileStack className="h-4 w-4 mr-2" />
              Bulk Actions ({selectedWorkflows.length})
            </Button>
          )}
          <Button onClick={() => navigate('/admin/templates/workflows/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workflow
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Workflows</p>
              <p className="text-2xl font-bold">{totalWorkflows}</p>
            </div>
            <Activity className="h-8 w-8 text-muted-foreground" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-green-600">{activeWorkflows}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Completion</p>
              <p className="text-2xl font-bold">{avgCompletionRate.toFixed(0)}%</p>
            </div>
            <BarChart3 className="h-8 w-8 text-muted-foreground" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Inactive</p>
              <p className="text-2xl font-bold text-orange-600">{totalWorkflows - activeWorkflows}</p>
            </div>
            <TrendingDown className="h-8 w-8 text-orange-600" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="member_operations">Member Operations</SelectItem>
            <SelectItem value="asset_management">Asset Management</SelectItem>
            <SelectItem value="governance">Governance</SelectItem>
            <SelectItem value="financial_operations">Financial Operations</SelectItem>
            <SelectItem value="estate_planning">Estate Planning</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Workflows Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={selectedWorkflows.length === filteredWorkflows.length && filteredWorkflows.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedWorkflows(filteredWorkflows.map(w => w.id));
                    } else {
                      setSelectedWorkflows([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Phases</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Completion</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead className="w-[200px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Loading workflows...
                </TableCell>
              </TableRow>
            ) : filteredWorkflows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No workflows found
                </TableCell>
              </TableRow>
            ) : (
              filteredWorkflows.map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedWorkflows.includes(workflow.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedWorkflows([...selectedWorkflows, workflow.id]);
                        } else {
                          setSelectedWorkflows(selectedWorkflows.filter(id => id !== workflow.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-medium max-w-xs">
                    <div>
                      <p>{workflow.title}</p>
                      <p className="text-xs text-muted-foreground">{workflow.workflow_key}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{workflow.category.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>{Array.isArray(workflow.phases) ? workflow.phases.length : 0}</TableCell>
                  <TableCell>{workflow.usage_count || 0}x</TableCell>
                  <TableCell>
                    {workflow.completion_rate ? (
                      <Badge variant={workflow.completion_rate > 70 ? "default" : "secondary"}>
                        {workflow.completion_rate.toFixed(0)}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => handleToggleActive(workflow.id, workflow.is_active)}>
                      <Badge className={workflow.is_active ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'}>
                        {workflow.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </TableCell>
                  <TableCell>v{workflow.version}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/templates/workflows/${workflow.id}`)}
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/templates/workflows/${workflow.id}/edit`)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/templates/workflows/${workflow.id}/analytics`)}
                        title="Analytics"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(workflow.id)}
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => exportWorkflow(workflow)}
                        title="Export"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(workflow.id, workflow.title)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <BulkWorkflowOperations
        open={showBulkOps}
        onClose={() => setShowBulkOps(false)}
        selectedWorkflows={selectedWorkflows}
        onComplete={() => {
          setSelectedWorkflows([]);
          loadWorkflows();
        }}
      />

      <ImportWorkflowDialog
        open={showImport}
        onClose={() => setShowImport(false)}
      />
    </div>
  );
}